/**
 * RequireAdmin Component
 *
 * Protects routes that require admin wallet access.
 * Redirects unauthorized users to the home page.
 *
 * Supports both:
 * - Standard wallet adapter (Phantom, etc.)
 * - Turnkey embedded wallet
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { isAdminWallet } from '@/lib/meteora/feeMetricsClient';
import { Loader2, Shield } from 'lucide-react';

interface RequireAdminProps {
  children: ReactNode;
}

export function RequireAdmin({ children }: RequireAdminProps) {
  // Check both wallet systems
  const { connected: adapterConnected, publicKey: adapterPublicKey, connecting: adapterConnecting } = useWallet();
  const {
    connected: turnkeyConnected,
    publicKey: turnkeyPublicKey,
    isLoading: turnkeyLoading,
    isAuthenticated: turnkeyAuthenticated,
  } = useTurnkeyWallet();
  const navigate = useNavigate();

  // Use whichever wallet is connected (prefer Turnkey if both)
  const connected = turnkeyConnected || adapterConnected;
  const publicKey = turnkeyPublicKey || adapterPublicKey;

  // Consider "connecting" if:
  // - Turnkey is loading
  // - Adapter is connecting
  // - Turnkey is authenticated but wallet hasn't loaded yet (gap between auth and wallet data)
  const connecting = turnkeyLoading || adapterConnecting || (turnkeyAuthenticated && !turnkeyConnected);

  const walletAddress = publicKey?.toBase58() ?? null;
  const isAdmin = connected && walletAddress && isAdminWallet(walletAddress);

  useEffect(() => {
    // Don't redirect while still connecting
    if (connecting) return;

    // If not connected or not admin, redirect after a longer delay
    // to allow wallet state to fully settle
    if (!connected || !isAdmin) {
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [connected, isAdmin, connecting, navigate]);

  // Show loading state while connecting
  if (connecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">Connecting wallet...</p>
      </div>
    );
  }

  // Show unauthorized state briefly before redirect
  if (!connected || !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Shield className="w-16 h-16 text-destructive" />
        <h1 className="text-2xl font-bold">Not Authorized</h1>
        <p className="text-muted-foreground">
          {!connected
            ? 'Please connect your wallet to access this page.'
            : 'This page is restricted to admin wallets only.'}
        </p>
        <p className="text-sm text-muted-foreground">Redirecting...</p>
      </div>
    );
  }

  // User is admin - render children
  return <>{children}</>;
}
